import {
  numberOrNull,
  type ProductSection,
  type ShiftFormValidationIssue,
  validationMessage,
} from "./form-state";
import { ShiftFormEvents } from "./ShiftFormEvents";
import { ShiftFormMeasurements } from "./ShiftFormMeasurements";
import { ShiftFormTimeFields } from "./ShiftFormTimeFields";

type Props = {
  productionDate?: string | null;
  product: ProductSection;
  productIndex: number;
  speedUnit: string | null;
  speedUnitHint: string;
  validationIssues: readonly ShiftFormValidationIssue[];
  onChange: (product: ProductSection) => void;
};
export function ShiftFormProduct({
  product,
  productIndex,
  productionDate = null,
  speedUnit,
  speedUnitHint,
  validationIssues,
  onChange,
}: Props) {
  return (
    <div className="shift-product-sheet" data-shift-diff-path={`products.${productIndex}`}>
      <PaperHeading title="Çalışma Zaman" />
      <p className="shift-required-section"><strong>* Zorunlu:</strong> Her satırda zamanı veya karşısındaki gerçekleşmeme sebebini yazın.</p>
      <ShiftFormTimeFields
        productionDate={productionDate}
        label="Çalışma Zaman"
        validationIssues={validationIssues}
        validationPrefix={`products.${productIndex}`}
        required
        values={{
          startedAt: product.machineStartedAt,
          delayReason:
            product.delayReason ?? product.machineStartNotOccurredReason,
          firstProductAt: product.firstProductAt,
          firstProductDelayReason: product.firstProductNotOccurredReason,
        }}
        onChange={(values) =>
          onChange({
            ...product,
            machineStartedAt: values.startedAt,
            delayReason: values.delayReason,
            delayed: Boolean(values.delayReason),
            machineStartNotOccurredReason: values.delayReason,
            firstProductAt: values.firstProductAt,
            firstProductNotOccurredReason: values.firstProductDelayReason,
          })
        }
      />
      <PaperHeading title="Makina / Ürün / Ortam Parametreleri" />
      <ShiftFormMeasurements
        measurements={product.measurements}
        speedUnit={speedUnit}
        speedUnitHint={speedUnitHint}
        validationIssues={validationIssues}
        validationPrefix={`products.${productIndex}`}
        onChange={(measurement) =>
          onChange({
            ...product,
            measurements: product.measurements.map((item) =>
              item.key === measurement.key ? measurement : item,
            ),
          })
        }
      />
      <ShiftFormEvents
        productionDate={productionDate}
        product={product}
        onChange={onChange}
        speedUnit={speedUnit}
        validationIssues={validationIssues}
        validationPrefix={`products.${productIndex}`}
      />
      <ShiftFormTimeFields
        productionDate={productionDate}
        label="Ürün Değişim Sonrası"
        separate
        validationIssues={validationIssues}
        validationPrefix={`products.${productIndex}`}
        required={product.productChanges.length > 0}
        values={{
          startedAt: product.postChangeMachineStartedAt,
          delayReason: product.postChangeDelayReason,
          firstProductAt: product.postChangeFirstProductAt,
          firstProductDelayReason: product.postChangeFirstProductDelayReason,
        }}
        onChange={(values) =>
          onChange({
            ...product,
            postChangeMachineStartedAt: values.startedAt,
            postChangeDelayReason: values.delayReason,
            postChangeFirstProductAt: values.firstProductAt,
            postChangeFirstProductDelayReason: values.firstProductDelayReason,
          })
        }
      />
      <Quantities product={product} onChange={onChange} validationIssues={validationIssues} validationPrefix={`products.${productIndex}`} />
    </div>
  );
}

function Quantities({
  product,
  onChange,
  validationIssues,
  validationPrefix,
}: Pick<Props, "product" | "onChange" | "validationIssues"> & { validationPrefix: string }) {
  const quantities = product.quantities;
  const set = <K extends keyof typeof quantities>(
    key: K,
    value: (typeof quantities)[K],
  ) => onChange({ ...product, quantities: { ...quantities, [key]: value } });
  const wasteExists = [
    quantities.scrapKg,
    quantities.animalFeedKg,
    quantities.reelWasteQuantity,
    quantities.boxWasteCount,
    quantities.cartonWasteCount,
    quantities.labelWasteCount,
  ].some((value) => value !== null && value > 0);
  return (
    <>
      <PaperHeading title="Ürün Fire-Iskarta Oranları" />
      <div className="shift-paper-scroll" data-shift-validation={`${validationPrefix}.wasteReason`} tabIndex={-1}>
        <table className="shift-paper-table shift-quantity-table">
          <thead>
            <tr>
              {[
                "Iskarta",
                "Hayvan Yemi (GSF)",
                "Bobin Fire",
                "Kutu Fire",
                "Koli Fire",
                "Etiket Fire",
              ].map((label) => (
                <th key={label} scope="col">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <QuantityCell
                label="Iskarta"
                diffPath={`${validationPrefix}.quantities.scrapKg`}
                unit="kg"
                value={quantities.scrapKg}
                onChange={(value) => set("scrapKg", value)}
              />
              <QuantityCell
                label="Hayvan Yemi (GSF)"
                diffPath={`${validationPrefix}.quantities.animalFeedKg`}
                unit="kg"
                value={quantities.animalFeedKg}
                onChange={(value) => set("animalFeedKg", value)}
              />
              <QuantityCell
                label="Bobin Fire"
                diffPath={`${validationPrefix}.quantities.reelWasteQuantity`}
                unit="kg"
                value={quantities.reelWasteQuantity}
                onChange={(value) => set("reelWasteQuantity", value)}
              />
              <QuantityCell
                label="Kutu Fire"
                diffPath={`${validationPrefix}.quantities.boxWasteCount`}
                unit="adet"
                integer
                value={quantities.boxWasteCount}
                onChange={(value) => set("boxWasteCount", value)}
              />
              <QuantityCell
                label="Koli Fire"
                diffPath={`${validationPrefix}.quantities.cartonWasteCount`}
                unit="adet"
                integer
                value={quantities.cartonWasteCount}
                onChange={(value) => set("cartonWasteCount", value)}
              />
              <QuantityCell
                label="Etiket Fire"
                diffPath={`${validationPrefix}.quantities.labelWasteCount`}
                unit="adet"
                integer
                value={quantities.labelWasteCount}
                onChange={(value) => set("labelWasteCount", value)}
              />
            </tr>
          </tbody>
        </table>
        <table className="shift-paper-table shift-waste-reason-table">
          <thead>
            <tr>
              <th scope="col">Sebebi{wasteExists ? <RequiredMark /> : null}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <textarea
                  data-shift-validation={`${validationPrefix}.wasteReason`}
                  data-shift-diff-path={`${validationPrefix}.quantities.wasteReason`}
                  aria-invalid={Boolean(validationMessage(validationIssues, `${validationPrefix}.wasteReason`))}
                  aria-describedby={validationMessage(validationIssues, `${validationPrefix}.wasteReason`) ? `${validationPrefix}-waste-error` : undefined}
                  aria-label="Fire-Iskarta / Sebebi"
                  rows={2}
                  required={wasteExists}
                  placeholder="Varsa fire veya ıskarta nedenini yazın"
                  value={quantities.wasteReason ?? ""}
                  onChange={(event) =>
                    set("wasteReason", event.target.value || null)
                  }
                />
                <InlineError id={`${validationPrefix}-waste-error`} message={validationMessage(validationIssues, `${validationPrefix}.wasteReason`)} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <PaperHeading title="Çıkan Ürün Miktarı" />
      <div className="shift-required-section" data-shift-validation={`${validationPrefix}.actualQuantity`} tabIndex={-1}>
        <strong>* Zorunlu:</strong> En az bir vardiya sonu çıkan miktar girin.
        <InlineError message={validationMessage(validationIssues, `${validationPrefix}.actualQuantity`)} />
      </div>
      <div className="shift-paper-scroll">
        <table className="shift-paper-table shift-output-table">
          <thead>
            <tr>
              <th scope="col">
                Koli
              </th>
              <th scope="col">
                Adet
              </th>
              <th scope="col">
                Kilogram
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <QuantityCell
                label="Vardiya Sonu Çıkan / Koli"
                diffPath={`${validationPrefix}.quantities.actualCarton`}
                integer
                value={quantities.actualCarton}
                onChange={(value) => set("actualCarton", value)}
              />
              <QuantityCell
                label="Vardiya Sonu Çıkan / Adet"
                diffPath={`${validationPrefix}.quantities.actualCount`}
                integer
                value={quantities.actualCount}
                onChange={(value) => set("actualCount", value)}
              />
              <QuantityCell
                label="Vardiya Sonu Çıkan / Kilogram"
                diffPath={`${validationPrefix}.quantities.actualKg`}
                value={quantities.actualKg}
                onChange={(value) => set("actualKg", value)}
              />
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

function InlineError({ id, message }: { id?: string; message: string | null }) {
  return message ? <small id={id} className="shift-field-error" role="alert">{message}</small> : null;
}
function RequiredMark() {
  return <span className="shift-required-mark" aria-label="zorunlu"> *</span>;
}
function PaperHeading({ title }: { title: string }) {
  return <div className="shift-paper-heading">{title}</div>;
}
function QuantityCell({
  label,
  diffPath,
  unit,
  value,
  integer = false,
  onChange,
}: {
  label: string;
  diffPath?: string;
  unit?: string;
  value: number | null;
  integer?: boolean;
  onChange: (value: number | null) => void;
}) {
  return (
    <td data-label={label}>
      <input
        data-shift-diff-path={diffPath}
        aria-label={label}
        placeholder={unit ? `Miktar (${unit})` : "Miktar yazın"}
        type="number"
        inputMode={integer ? "numeric" : "decimal"}
        min="0"
        step={integer ? "1" : "any"}
        value={value ?? ""}
        onChange={(event) => onChange(numberOrNull(event.target.value))}
      />
    </td>
  );
}
